import Swiper from 'swiper';
import { Pagination, Mousewheel, FreeMode, Scrollbar } from 'swiper/modules';
import { Fancybox } from '@fancyapps/ui/';

const MOBILE = '(max-width: 769px)';

class Product {
	constructor(el) {
		this.container = el;
		this.swiper = null;
		this.mm = window.matchMedia(MOBILE);
		this.zoomSlides = [];

		if (!this.container) return;

		this.init();
		this.mm.addEventListener('change', this.handleResize.bind(this));
	}

	init() {
		this.addCart();
		this.collectionSlider();

		if (!this.mm.matches) {
			this.initZoomEffect();
			return;
		}

		const sliderElement = this.container.querySelector('[data-product-slider]');
		if (!sliderElement) return;

		const sliderContainer = sliderElement.querySelector('.swiper');
		const pagination = sliderContainer?.nextElementSibling;

		if (!sliderContainer || !pagination) return;

		this.swiper = new Swiper(sliderContainer, {
			modules: [Pagination],
			slidesPerView: 1,
			spaceBetween: 10,
			pagination: {
				el: pagination,
				type: 'bullets',
				clickable: true,
			},
		});

		this.initMobileZoom();
	}

	handleResize() {
		if (this.mm.matches) {
			this.removeZoomEffect();
			if (!this.swiper) {
				this.init();
			}
		} else {
			if (this.swiper) {
				this.swiper.destroy(true, true);
				this.swiper = null;
			}
			this.initZoomEffect();
		}
	}

	initZoomEffect() {
		this.zoomSlides = Array.from(this.container.querySelectorAll('.product-slider__slide'));
		this.zoomSlides.forEach((slide) => {
			const img = slide.querySelector('img');
			const zoomInIcon = slide.querySelector('._zoom-in');
			const zoomOutIcon = slide.querySelector('._zoom-out');
			const link = slide.querySelector('[data-fancybox="product"]');
			let rafId = null;
			let lastXPercent = 50;
			let lastYPercent = 50;
			const updateTransformOrigin = () => {
				img.style.transformOrigin = `${lastXPercent}% ${lastYPercent}%`;
				rafId = null;
			};
			const onClick = (e) => {
				const rect = slide.getBoundingClientRect();
				lastXPercent = ((e.clientX - rect.left) / rect.width) * 100;
				lastYPercent = ((e.clientY - rect.top) / rect.height) * 100;
				const isZoomed = slide.classList.toggle('_zoomed');
				slide.dataset.zoom = isZoomed ? 'out' : 'in';
				if (isZoomed) {
					slide.classList.remove('_zooming-out');
					zoomInIcon.style.opacity = '0';
					zoomOutIcon.style.opacity = '1';
					img.style.transformOrigin = `${lastXPercent}% ${lastYPercent}%`;
				} else {
					slide.classList.add('_zooming-out');
					zoomInIcon.style.opacity = '1';
					zoomOutIcon.style.opacity = '0';
					setTimeout(() => {
						img.style.transformOrigin = 'center center';
						setTimeout(() => {
							slide.classList.remove('_zooming-out');
						}, 800);
					}, 0);
				}
			};
			const onMouseMove = (e) => {
				const rect = slide.getBoundingClientRect();
				let clientX, clientY;
				if (e.type.startsWith('touch')) {
					const touch = e.touches[0];
					clientX = touch.clientX;
					clientY = touch.clientY;
				} else {
					clientX = e.clientX;
					clientY = e.clientY;
				}
				const offsetX = 20;
				const offsetY = 20;
				const x = clientX - rect.left - offsetX;
				const y = clientY - rect.top - offsetY;
				zoomInIcon.style.left = `${x}px`;
				zoomInIcon.style.top = `${y}px`;
				zoomOutIcon.style.left = `${x}px`;
				zoomOutIcon.style.top = `${y}px`;
				if (!slide.classList.contains('_zoomed')) return;
				// --- SAFE-ЗОНА ---
				//  Процент от размеров изображения (не фиксированные пиксели)
				const SAFE_ZONE_PERCENT = 15;
				const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
				let xPercent = ((clientX - rect.left) / rect.width) * 100;
				let yPercent = ((clientY - rect.top) / rect.height) * 100;
				// Добавляем плавное "смягчение" на краях (safe zone)
				const applySafeZone = (pos, safeZone) => {
					if (pos < safeZone) {
						const ratio = (safeZone - pos) / safeZone;
						pos -= ratio * safeZone * 0.5; // тянем наружу плавно
					} else if (pos > 100 - safeZone) {
						const ratio = (pos - (100 - safeZone)) / safeZone;
						pos += ratio * safeZone * 0.5;
					}
					return pos;
				};
				xPercent = applySafeZone(xPercent, SAFE_ZONE_PERCENT);
				yPercent = applySafeZone(yPercent, SAFE_ZONE_PERCENT);
				lastXPercent = clamp(xPercent, -10, 110);
				lastYPercent = clamp(yPercent, -10, 110);
				if (!rafId) rafId = requestAnimationFrame(updateTransformOrigin);
			};
			// блокируем скролл страницы, когда активен зум
			const onTouchMove = (e) => {
				if (slide.classList.contains('_zoomed')) {
					e.preventDefault();
				}
				onMouseMove(e);
			};
			const onMouseEnter = () => {
				if (!slide.classList.contains('_zoomed')) {
					zoomInIcon.style.opacity = '1';
					zoomOutIcon.style.opacity = '0';
				}
			};
			const onMouseLeave = () => {
				slide.classList.remove('_zoomed');
				slide.classList.add('_zooming-out');
				slide.dataset.zoom = 'in';
				setTimeout(() => {
					img.style.transformOrigin = 'center center';
					setTimeout(() => {
						slide.classList.remove('_zooming-out');
					}, 800);
				}, 0);
				zoomInIcon.style.opacity = '0';
				zoomOutIcon.style.opacity = '0';
			};
			const onLinkClick = (e) => {
				e.preventDefault();
			};
			slide.addEventListener('click', onClick);
			slide.addEventListener('mousemove', onMouseMove);
			slide.addEventListener('touchmove', onTouchMove, { passive: false });
			slide.addEventListener('mouseenter', onMouseEnter);
			slide.addEventListener('mouseleave', onMouseLeave);
			link?.addEventListener('click', onLinkClick);
			slide._zoomHandlers = { onClick, onMouseMove, onTouchMove, onMouseEnter, onMouseLeave, onLinkClick };
		});
	}

	removeZoomEffect() {
		this.zoomSlides.forEach((slide) => {
			const link = slide.querySelector('[data-fancybox="product"]');
			const { onClick, onMouseMove, onTouchMove, onMouseLeave, onMouseEnter, onLinkClick } = slide._zoomHandlers || {};
			if (onClick) slide.removeEventListener('click', onClick);
			if (onMouseMove) slide.removeEventListener('mousemove', onMouseMove);
			if (onTouchMove) slide.removeEventListener('touchmove', onTouchMove);
			if (onMouseLeave) slide.removeEventListener('mouseleave', onMouseLeave);
			if (onMouseEnter) slide.removeEventListener('mouseenter', onMouseEnter);
			if (onLinkClick) link?.removeEventListener('click', onLinkClick);
			delete slide._zoomHandlers;
			slide.classList.remove('_zoomed');
		});
		this.zoomSlides = [];
	}

	addCart() {
		const cartGroups = this.container.querySelectorAll(
			'.product-prices__cart-buttons, .product-bottom-price__cart-buttons',
		);

		const productSlides = this.container.querySelectorAll('[data-product-slide]');

		if (!cartGroups.length || !productSlides.length) return;

		cartGroups.forEach((group) => {
			const cartButton = group.querySelector('[data-cart-button]');
			const productCounter = group.querySelector('[data-cart-counter]');

			if (!cartButton || !productCounter) return;

			cartButton.addEventListener('click', () => {
				productSlides.forEach((slide) => {
					slide.classList.add('_active');

					requestAnimationFrame(() => {
						setTimeout(() => {
							slide.classList.remove('_active');
						}, 200);
					});
				});

				productCounter.classList.add('_active');
				cartButton.classList.add('_active');
			});

			const minusButton = productCounter.querySelector('[data-counter-minus]');
			const value = productCounter.querySelector('[data-counter-value]');

			minusButton?.addEventListener('click', () => {
				if (value.textContent == 1 || value.textContent == 0) {
					productCounter.classList.remove('_active');
					cartButton.classList.remove('_active');
				}
			});
		});
	}

	collectionSlider() {
		const collectionSlider = this.container.querySelector('[data-product-collections]');

		if (!collectionSlider) return;

		this.collectionsSwiper = new Swiper(collectionSlider, {
			slidesPerView: 'auto',
			spaceBetween: 4,
			direction: 'vertical',
			freeMode: true,
			mousewheel: true,
			modules: [Mousewheel, FreeMode, Scrollbar],
			scrollbar: {
				el: '.swiper-scrollbar',
				draggable: true,
				hide: true,
			},
			on: {
				progress: (swiper, progress) => {
					this.handleCollectionsScroll(progress);
				},
				slideChange: (swiper) => {
					this.handleCollectionsScroll(swiper.progress);
				},
			},
		});
	}

	handleCollectionsScroll(progress) {
		const fadeElement = this.container.querySelector('.product__collections-fade');

		if (!fadeElement) return;

		const isElementVisible = () => {
			const style = window.getComputedStyle(fadeElement);
			return style.display !== 'none' && style.visibility !== 'hidden';
		};

		if (!isElementVisible()) return;

		if (progress > 0) {
			fadeElement.classList.add('_fade-visible');
		} else {
			fadeElement.classList.remove('_fade-visible');
		}
	}

	initMobileZoom() {
		Fancybox.bind('[data-fancybox="product"]', {});
	}
}

document.querySelectorAll('[data-product]').forEach((el) => new Product(el));
export default Product;
